import { useCallback, useState } from 'react';

import { useStoreContext } from 'features/store';
import { useIsStoreInMobileOrderDriveThruStores } from 'features/store/hooks/use-is-store-in-mobile-order-drive-thru-stores';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { useIntl } from 'react-intl';
import { ImageSourcePropType } from 'react-native';

import { toaster } from 'components/toaster/toaster';
import useEffectOnce from 'hooks/use-effect-once';
import { useIsMobileOrderForDriveThru } from 'hooks/use-is-mobile-order-for-drive-thru';
import useIsSignUpAfterCart from 'hooks/use-is-sign-up-after-cart';
import { useIsVehicleDescriptionEnabled } from 'pages/cart/hooks/use-is-vehicle-description-enabled';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logButtonClick,
  logEvent,
} from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { useFlyBuyContext } from 'state/flybuy';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { ServiceMode } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { hapticSelection } from 'utils/haptic';
import { getHeadingForServiceMode, getPreferredDriveThruServiceMode } from 'utils/service-mode';

import { ServiceModeOption, UseServiceModeWidget } from '../components/service-mode/types';

export const SERVICE_MODE_ORDER: ServiceMode[] = [
  ServiceMode.TAKEOUT,
  ServiceMode.DRIVE_THRU,
  ServiceMode.MOBILE_ORDER_DRIVE_THRU,
  ServiceMode.EAT_IN,
  ServiceMode.CURBSIDE,
];

const getServiceModeDescriptionTextId = (serviceMode: ServiceMode): string => {
  switch (serviceMode) {
    case ServiceMode.TAKEOUT:
      return 'serviceModeDetailsPickupDescriptionV2';
    case ServiceMode.CURBSIDE:
      return 'serviceModeDetailsCurbsideDescriptionV2';
    case ServiceMode.DRIVE_THRU:
      return 'serviceModeDetailsDriveThruDescriptionV2';
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return 'serviceModeDetailsMobileOrderDriveThruDescriptionV2';
    case ServiceMode.EAT_IN:
      return 'serviceModeDetailsDineInDescriptionV2';
    default:
      return '';
  }
};

const serviceModeImages: Partial<Record<ServiceMode, ImageSourcePropType>> = {
  [ServiceMode.DRIVE_THRU]: require('src/pages/cart/details/images/drive-thru.jpg'),
  [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: require('src/pages/cart/details/images/drive-thru.jpg'),
  [ServiceMode.TAKEOUT]: require('src/pages/cart/details/images/take-out.jpg'),
  [ServiceMode.EAT_IN]: require('src/pages/cart/details/images/dine-in.jpg'),
  [ServiceMode.CURBSIDE]: require('src/pages/cart/details/images/curbside.jpg'),
};

const useVehicleDescription = () => {
  const {
    setVehicleDescription,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
  } = useCartContext();

  const { orderBayValue } = useServiceModeContext();
  const isSignUpAfterCart = useIsSignUpAfterCart();
  const isVehicleDescriptionEnabled = useIsVehicleDescriptionEnabled();

  return {
    setVehicleDescription,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
    orderBayValue,
    isSignUpAfterCart,
    displayVehicleDescription: isVehicleDescriptionEnabled,
  };
};

export const useServiceModeWidget = (): UseServiceModeWidget => {
  const { formatMessage } = useIntl();

  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);
  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );
  const { isServiceModeAvailable, isStoreAvailableForPickup } = useStoreAvailabilityFns();
  const { store } = useStoreContext();

  const { isReadyWithTimedFire: isFlyBuyReadyWithTimedFire } = useFlyBuyContext();
  const { selectServiceMode, serviceMode: activeServiceMode, isDelivery } = useServiceModeContext();
  const isMobileOrderDriveThruEnabledForStore = useIsStoreInMobileOrderDriveThruStores(
    store.number
  );

  const [hasInvalidDeliveryOptions, setHasInvalidDeliveryOptions] = useState(false);
  // Note that this happens asynchronously, so the initial value of `isPickupTimeAvailable` will be `undefined`.
  const isMobileOrderForDriveThru = useIsMobileOrderForDriveThru(activeServiceMode, store.number);
  const {
    setVehicleDescription,
    vehicleDescription: { value: vehicleDescriptionValue, hasError: vehicleDescriptionHasError },
    displayVehicleDescription,
  } = useVehicleDescription();

  const isDisabled = !isDelivery && !isStoreAvailableForPickup(store);
  const [isServiceModeSelectorOpen, setIsServiceModeSelectorOpen] = useState(false);

  // Present the Pickup Time Selector if:
  // - Order injection is TIMED_FIRE
  // - FlyBuy is not active or variation is "EnabledWithoutTimedFire"
  const isOrderTimedFire =
    orderInjectFlow === OrderInjectionFlowVariations.TIMED_FIRE && !isFlyBuyReadyWithTimedFire;

  const handleOpenServiceModeSelector = () => {
    logButtonClick(
      {
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: CustomEventNames.BUTTON_CLICK_SERVICE_MODE_WIDGET,
        },
      },
      { logDuplicateClickEvent: true }
    );

    setIsServiceModeSelectorOpen(true);
  };

  const getServiceModeOptions = useCallback((): ServiceModeOption[] => {
    return SERVICE_MODE_ORDER.flatMap(serviceMode => {
      if (
        serviceMode === ServiceMode.DRIVE_THRU ||
        serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU
      ) {
        const preferredDriveThruServiceMode = getPreferredDriveThruServiceMode(
          isMobileOrderDriveThruEnabledForStore,
          isServiceModeAvailable,
          store
        );

        if (serviceMode !== preferredDriveThruServiceMode) {
          return [];
        }
      }

      const serviceModeHeadingAndIcon = getHeadingForServiceMode(
        formatMessage,
        isFastLaneEnabled,
        isMobileOrderForDriveThruLaneEnabled,
        serviceMode,
        false
      );

      const descriptionTextId = getServiceModeDescriptionTextId(serviceMode);
      return {
        icon: serviceModeHeadingAndIcon?.icon ?? '',
        heading: serviceModeHeadingAndIcon?.heading ?? '',
        name: serviceMode,
        image: serviceModeImages[serviceMode],
        description: formatMessage({ id: descriptionTextId }),
        isSelected: activeServiceMode === serviceMode,
        isAvailable: isServiceModeAvailable(store, serviceMode),
      };
    });
  }, [
    formatMessage,
    isFastLaneEnabled,
    isMobileOrderForDriveThruLaneEnabled,
    activeServiceMode,
    isServiceModeAvailable,
    store,
    isMobileOrderDriveThruEnabledForStore,
  ]);

  const handleSelectServiceMode = useCallback(
    (mode: ServiceMode) => {
      hapticSelection();
      selectServiceMode(mode);
      logEvent(CustomEventNames.BUTTON_CLICK_PICKUP, EventTypes.Other);
      setIsServiceModeSelectorOpen(false);
    },
    [selectServiceMode]
  );

  const serviceModeOptions = getServiceModeOptions();

  const selectedServiceMode = serviceModeOptions.find(serviceMode => serviceMode.isSelected);
  const selectedServiceModeAccessibilityLabel = `${formatMessage({
    id: 'currentServiceMode',
  })}: ${selectedServiceMode?.heading}`;

  // If actual service mode is not available, then try to select the next available service mode
  useEffectOnce(() => {
    if (isDelivery) {
      return;
    }

    if (!selectedServiceMode?.isAvailable) {
      const nextAvailableServiceMode = serviceModeOptions.find(
        serviceMode => serviceMode.isAvailable
      );

      if (nextAvailableServiceMode) {
        selectServiceMode(nextAvailableServiceMode.name);
        toaster.informative(
          formatMessage({
            id: 'serviceModeSwitchedAutomatically',
            defaultMessage: `${selectedServiceMode?.heading} is unavailable. Switched to ${nextAvailableServiceMode?.heading}. You can change it if needed.`,
          })
        );
      }
    }
  });

  return {
    selectedServiceMode,
    selectedServiceModeAccessibilityLabel,
    serviceModeOptions,
    handleOpenServiceModeSelector,
    handleSelectServiceMode,
    isServiceModeSelectorOpen,
    setIsServiceModeSelectorOpen,
    isMobileOrderForDriveThru,
    setVehicleDescription,
    vehicleDescriptionValue,
    vehicleDescriptionHasError,
    displayVehicleDescription,
    isOrderTimedFire,
    hasInvalidDeliveryOptions,
    setHasInvalidDeliveryOptions,
    isDisabled,
  };
};
